import { StoresContext } from '@App/contexts'
import Navigation from '@App/navigation'
import { PARAM } from '@App/navigation/param'
import { useDocumentUpload } from '@App/services/useDocumentUpload'
import { DocumentUploadFormButtonsOverride } from '@Components/DocumentUploadForm'
import {
    attachExistingOverclaimDocument,
    generateOverclaimDocumentUploadUrl,
    searchOverclaimDocuments,
} from '@App/api/routes/overclaim'
import { UnifiedDocument } from '@Entities/document/document-to-upload'
import { OverclaimDocumentSearchResult } from '@Entities/overclaim/overclaim-document-search-result'
import { PaginationBody } from '@Entities/pagination'
import { ResultPage } from '@Entities/result-page'
import {
    UPLOAD_DOCUMENTS_FORM_ACCEPTED_FILE_TYPES,
    UPLOAD_DOCUMENTS_FORM_MAX_FILE_SIZE_IN_BYTES,
} from '@Scenes/RightsholderScene/RightsholderOverclaim/OverclaimView/OverclaimDocumentUploadForm'
import { Maybe } from '@Types'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import {
    createDocumentSearchCriteriaStore,
    DEFAULT_PAGINATION,
    DEFAULT_RECENTLY_USED_PAGINATION,
    normalizeOverclaimDocumentSearchQuery,
    OverclaimDocumentSearchQuery,
} from './search-config'

export const useOverclaimDocumentUploadPage = () => {
    const { overclaimViewStore, preloaderStore, rightsholderStore, popupStore } = useContext(StoresContext)
    const params = useParams<typeof PARAM>()
    const navigate = useNavigate()
    const overclaimHeaderId = params.overclaimHeaderId && +params.overclaimHeaderId
    const rightsholderId = rightsholderStore.currentRightsholderId
    const uploadButtonsOverrideRef = useRef<DocumentUploadFormButtonsOverride>(null)
    const [recentlyUsedDocumentsPage, setRecentlyUsedDocumentsPage] =
        useState<ResultPage<OverclaimDocumentSearchResult>>()
    const [searchedDocumentsPage, setSearchedDocumentsPage] = useState<ResultPage<OverclaimDocumentSearchResult>>()
    const [recentlyUsedPagination, setRecentlyUsedPagination] = useState<PaginationBody>(
        DEFAULT_RECENTLY_USED_PAGINATION
    )
    const [searchPagination, setSearchPagination] = useState<PaginationBody>(DEFAULT_PAGINATION)
    const [searchCriteriaStore, setSearchCriteriaStore] = useState(createDocumentSearchCriteriaStore)
    const [recentlyUsedLoading, setRecentlyUsedLoading] = useState(false)
    const [searchLoading, setSearchLoading] = useState(false)
    const [hasRunSearch, setHasRunSearch] = useState(false)
    const [activeSearchQuery, setActiveSearchQuery] = useState<Partial<OverclaimDocumentSearchQuery>>({})

    const generateS3PresignedUrl = async (
        fileName: string,
        fileSize: number,
        fileDescription: Maybe<string>,
        fileHash?: Maybe<string>
    ) => {
        if (!overclaimHeaderId) {
            throw new Error('Overclaim header ID is not defined')
        }
        const response = await generateOverclaimDocumentUploadUrl(
            rightsholderId,
            overclaimHeaderId,
            fileName,
            fileSize,
            fileDescription,
            fileHash
        )
        return response.data
    }

    const attachExistingDocuments = async (uploadedFiles: UnifiedDocument[]) => {
        if (!overclaimHeaderId) {
            throw new Error('Overclaim header ID is not defined')
        }
        const existingDocuments = uploadedFiles.filter((document) => document.isExistingSelection)
        await Promise.all(
            existingDocuments.map((document) =>
                attachExistingOverclaimDocument(
                    rightsholderId,
                    overclaimHeaderId,
                    document.existingMemberUploadId!,
                    document.description
                )
            )
        )
    }

    const uploadState = useDocumentUpload(
        generateS3PresignedUrl,
        UPLOAD_DOCUMENTS_FORM_ACCEPTED_FILE_TYPES,
        UPLOAD_DOCUMENTS_FORM_MAX_FILE_SIZE_IN_BYTES,
        uploadButtonsOverrideRef,
        undefined,
        async () => {
            await attachExistingDocuments(uploadState.uploadedFiles)
            await overclaimViewStore.loadOverclaimDocumentsForCurrentOverclaimViewItem()
            await overclaimViewStore.getOverclaimHistoryByHeaderId()
            navigate(`${Navigation.rightholder.overclaim.view(rightsholderId, overclaimHeaderId!)}#documents`)
        },
        undefined,
        true
    )

    const loadRecentlyUsedDocuments = async (pagination = recentlyUsedPagination) => {
        if (!overclaimHeaderId) {
            return
        }
        setRecentlyUsedLoading(true)
        try {
            const response = await searchOverclaimDocuments(rightsholderId, overclaimHeaderId, pagination, {})
            setRecentlyUsedDocumentsPage(response.data)
            setRecentlyUsedPagination(pagination)
        } finally {
            setRecentlyUsedLoading(false)
        }
    }

    const loadSearchedDocuments = async (
        searchQuery: Partial<OverclaimDocumentSearchQuery>,
        pagination = searchPagination
    ) => {
        if (!overclaimHeaderId) {
            return
        }
        setSearchLoading(true)
        setHasRunSearch(true)
        try {
            const response = await searchOverclaimDocuments(
                rightsholderId,
                overclaimHeaderId,
                pagination,
                normalizeOverclaimDocumentSearchQuery(searchQuery)
            )
            setSearchedDocumentsPage(response.data)
            setSearchPagination(pagination)
            setActiveSearchQuery(searchQuery)
        } finally {
            setSearchLoading(false)
        }
    }

    useEffect(() => {
        if (
            !overclaimViewStore.overclaimViewItem ||
            (overclaimHeaderId && overclaimViewStore.overclaimViewItem.overclaimHeaderId !== overclaimHeaderId)
        ) {
            if (overclaimHeaderId) {
                preloaderStore.openMainPreloader(true)
                overclaimViewStore
                    .getOverclaimViewItemByHeaderId(overclaimHeaderId, rightsholderId)
                    .finally(() => preloaderStore.openMainPreloader(false))
            }
        }
        loadRecentlyUsedDocuments(DEFAULT_RECENTLY_USED_PAGINATION).finally()
    }, [])

    const selectedExistingDocumentIds = useMemo(
        () =>
            new Set(
                uploadState.uploadedFiles
                    .filter((document) => document.isExistingSelection && document.existingMemberUploadId)
                    .map((document) => document.existingMemberUploadId!)
            ),
        [uploadState.uploadedFiles]
    )

    const toggleExistingDocument = (document: OverclaimDocumentSearchResult, shouldSelect: boolean) => {
        const existingDocument = uploadState.uploadedFiles.find(
            (selectedDocument) => selectedDocument.existingMemberUploadId === document.memberUploadId
        )
        if (!shouldSelect && existingDocument) {
            uploadState.onRemove(existingDocument)
            return
        }
        if (shouldSelect) {
            uploadState.addDocuments([
                UnifiedDocument.fromExistingSelection({
                    memberUploadId: document.memberUploadId,
                    name: document.name,
                    size: document.size,
                    description: document.description,
                    lastModified: document.createdAt,
                }),
            ])
            popupStore.setAlertData('Your selection has been added to Your Selected Documents list', false)
        }
    }
    return {
        activeSearchQuery,
        hasRunSearch,
        overclaimViewItem: overclaimViewStore.overclaimViewItem,
        recentlyUsedDocumentsPage,
        recentlyUsedLoading,
        recentlyUsedPagination,
        rightsholderId,
        searchedDocumentsPage,
        searchCriteriaStore,
        searchLoading,
        searchPagination,
        selectedExistingDocumentIds,
        setSearchCriteriaStore,
        setSearchedDocumentsPage,
        setHasRunSearch,
        setActiveSearchQuery,
        toggleExistingDocument,
        uploadState,
        loadRecentlyUsedDocuments,
        loadSearchedDocuments,
    }
}
